import React from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';

/**
 * SEO Component for Freedom Contracting Group
 * 
 * Enhanced for better search rankings with:
 * - Improved keyword targeting
 * - Multiple schema support
 * - Local SEO optimization
 * - Open Graph and Twitter Cards
 * 
 * Usage:
 * <SEO 
 *   title="Commercial Construction Chicago" 
 *   description="Expert commercial construction services..." 
 *   path="/services"
 *   keywords="general contractor chicago, commercial construction"
 * />
 */

// Base configuration
const SITE_CONFIG = {
  siteName: 'Freedom Contracting Group',
  siteUrl: 'https://freedomcg.net',
  defaultTitle: 'Freedom Contracting Group | General Contractor Chicago | Commercial Construction',
  defaultDescription: 'Freedom Contracting Group is a leading general contractor in Chicago providing commercial construction, office remodeling, industrial warehouse construction, and construction management services. 20+ years experience, 1000+ projects. Call (312) 675-6000.',
  defaultKeywords: 'general contractor chicago, commercial construction chicago, construction companies chicago, commercial contractor chicago, general contractors, commercial office remodeling, construction management services, industrial construction chicago, building contractor chicago',
  twitterHandle: '@freedomcg',
  locale: 'en_US',
  themeColor: '#1e3a8a',
  logo: '/logo.webp',
  address: {
    street: '111 W Jackson Blvd, Suite 1700',
    city: 'Chicago',
    state: 'IL',
    zip: '60604',
    country: 'US'
  },
  phone: '+1-312-675-6000',
  phoneDisplay: '(312) 675-6000',
  email: 'hello@freedomcg.net'
};

// Schema.org Organization structured data
const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'GeneralContractor',
  '@id': `${SITE_CONFIG.siteUrl}/#organization`,
  name: SITE_CONFIG.siteName,
  alternateName: ['FCG', 'Freedom CG', 'Freedom Contracting'],
  url: SITE_CONFIG.siteUrl,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_CONFIG.siteUrl}${SITE_CONFIG.logo}`,
    width: '400',
    height: '400'
  },
  image: `${SITE_CONFIG.siteUrl}/fcbghero1.webp`,
  description: 'Freedom Contracting Group is a licensed general contractor in Chicago specializing in commercial construction, office remodeling, industrial construction, and construction management services.',
  telephone: SITE_CONFIG.phone,
  email: SITE_CONFIG.email,
  foundingDate: '2005',
  numberOfEmployees: {
    '@type': 'QuantitativeValue',
    minValue: 10,
    maxValue: 50
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE_CONFIG.address.street,
    addressLocality: SITE_CONFIG.address.city,
    addressRegion: SITE_CONFIG.address.state,
    postalCode: SITE_CONFIG.address.zip,
    addressCountry: SITE_CONFIG.address.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '41.8781',
    longitude: '-87.6298'
  },
  areaServed: [
    {
      '@type': 'City',
      name: 'Chicago',
      sameAs: 'https://en.wikipedia.org/wiki/Chicago'
    },
    {
      '@type': 'State',
      name: 'Illinois',
      sameAs: 'https://en.wikipedia.org/wiki/Illinois'
    },
    {
      '@type': 'Place',
      name: 'Chicagoland'
    }
  ],
  priceRange: '$$$$',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '18:00'
    }
  ],
  sameAs: [
    'https://www.facebook.com/freedomcg',
    'https://www.linkedin.com/company/freedomcg',
    'https://www.instagram.com/freedomcg'
  ],
  knowsAbout: [
    'Commercial Construction',
    'General Contracting',
    'Office Remodeling',
    'Industrial Construction',
    'Healthcare Construction',
    'Retail Construction',
    'Construction Management',
    'Tenant Improvements'
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Commercial Construction Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Commercial Building Construction',
          description: 'Full-service commercial building construction for office spaces, retail centers, and more in Chicago.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Office Remodeling',
          description: 'Commercial office remodeling, tenant improvements, and office build-outs in Chicago.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Industrial Warehouse Construction',
          description: 'Durable, efficient warehouse construction designed for manufacturing and distribution.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Healthcare Facility Construction',
          description: 'Specialized construction for hospitals, clinics, and medical centers.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Retail & Restaurant Construction',
          description: 'Retail store build-outs and restaurant construction in Chicago.'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Construction Management',
          description: 'End-to-end project coordination ensuring on-time, on-budget delivery.'
        }
      }
    ]
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5',
    reviewCount: '150',
    bestRating: '5',
    worstRating: '1'
  },
  review: [
    {
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: 'Chicago Business Owner'
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: '5',
        bestRating: '5'
      },
      reviewBody: 'Excellent commercial construction work. Professional team, on time, and within budget.'
    }
  ]
});

// Schema.org WebSite structured data
const getWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_CONFIG.siteUrl}/#website`,
  url: SITE_CONFIG.siteUrl,
  name: SITE_CONFIG.siteName,
  description: SITE_CONFIG.defaultDescription,
  publisher: {
    '@id': `${SITE_CONFIG.siteUrl}/#organization`
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_CONFIG.siteUrl}/search?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  },
  inLanguage: 'en-US'
});

// Schema.org BreadcrumbList
const getBreadcrumbSchema = (breadcrumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: crumb.url ? `${SITE_CONFIG.siteUrl}${crumb.url}` : undefined
  }))
});

// Local Business Schema for better local SEO
const getLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_CONFIG.siteUrl}/#localbusiness`,
  name: SITE_CONFIG.siteName,
  image: `${SITE_CONFIG.siteUrl}/fcbghero1.webp`,
  telephone: SITE_CONFIG.phone,
  email: SITE_CONFIG.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE_CONFIG.address.street,
    addressLocality: SITE_CONFIG.address.city,
    addressRegion: SITE_CONFIG.address.state,
    postalCode: SITE_CONFIG.address.zip,
    addressCountry: SITE_CONFIG.address.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '41.8781',
    longitude: '-87.6298'
  },
  url: SITE_CONFIG.siteUrl,
  priceRange: '$$$$',
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '18:00'
    }
  ],
  sameAs: [
    'https://www.facebook.com/freedomcg',
    'https://www.linkedin.com/company/freedomcg'
  ]
});

// Professional Service Schema
const getProfessionalServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'ProfessionalService',
  name: SITE_CONFIG.siteName,
  description: 'Licensed general contractor providing commercial construction services in Chicago',
  url: SITE_CONFIG.siteUrl,
  telephone: SITE_CONFIG.phone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE_CONFIG.address.street,
    addressLocality: SITE_CONFIG.address.city,
    addressRegion: SITE_CONFIG.address.state,
    postalCode: SITE_CONFIG.address.zip,
    addressCountry: SITE_CONFIG.address.country
  },
  areaServed: {
    '@type': 'GeoCircle',
    geoMidpoint: {
      '@type': 'GeoCoordinates',
      latitude: '41.8781',
      longitude: '-87.6298'
    },
    geoRadius: '80000'
  }
});

function SEO({
  title,
  description,
  keywords,
  path = '',
  image,
  imageAlt,
  type = 'website',
  article,
  noindex = false,
  breadcrumbs,
  additionalSchema
}) {
  const fullTitle = title 
    ? `${title} | ${SITE_CONFIG.siteName}`
    : SITE_CONFIG.defaultTitle;
  
  const metaDescription = description || SITE_CONFIG.defaultDescription;
  const metaKeywords = keywords || SITE_CONFIG.defaultKeywords;
  const canonicalUrl = `${SITE_CONFIG.siteUrl}${path}`;
  const metaImage = image || `${SITE_CONFIG.siteUrl}/fcbghero1.webp`;
  const metaImageAlt = imageAlt || 'Freedom Contracting Group - Commercial Construction Chicago';

  // Build schema array
  const schemas = [
    getOrganizationSchema(),
    getWebsiteSchema(),
    getLocalBusinessSchema(),
    getProfessionalServiceSchema()
  ];

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push(getBreadcrumbSchema(breadcrumbs));
  }

  // Add additional schemas if provided
  if (additionalSchema) {
    if (Array.isArray(additionalSchema)) {
      schemas.push(...additionalSchema);
    } else {
      schemas.push(additionalSchema);
    }
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      {/* Author */}
      <meta name="author" content="Freedom Contracting Group" />
      <meta name="publisher" content="Freedom Contracting Group" />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Language */}
      <html lang="en" />
      <meta name="language" content="English" />
      <meta name="content-language" content="en-US" />
      
      {/* Geo Tags for Local SEO */}
      <meta name="geo.region" content="US-IL" />
      <meta name="geo.placename" content="Chicago" />
      <meta name="geo.position" content="41.8781;-87.6298" />
      <meta name="ICBM" content="41.8781, -87.6298" />
      
      {/* Business Verification (add your verification codes here) */}
      {/* <meta name="google-site-verification" content="YOUR_GOOGLE_VERIFICATION_CODE" /> */}
      {/* <meta name="msvalidate.01" content="YOUR_BING_VERIFICATION_CODE" /> */}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:secure_url" content={metaImage} />
      <meta property="og:image:alt" content={metaImageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_CONFIG.siteName} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />
      
      {/* Facebook Specific */}
      <meta property="fb:app_id" content="" />
      
      {/* Article specific Open Graph tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.section} />
          {article.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:creator" content={SITE_CONFIG.twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:image:alt" content={metaImageAlt} />
      
      {/* Theme Color */}
      <meta name="theme-color" content={SITE_CONFIG.themeColor} />
      <meta name="msapplication-TileColor" content={SITE_CONFIG.themeColor} />
      <meta name="msapplication-navbutton-color" content={SITE_CONFIG.themeColor} />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      
      {/* Mobile */}
      <meta name="format-detection" content="telephone=yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={SITE_CONFIG.siteName} />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="application-name" content={SITE_CONFIG.siteName} />
      
      {/* Referrer */}
      <meta name="referrer" content="origin-when-cross-origin" />
      
      {/* Structured Data - All schemas in one script tag */}
      <script type="application/ld+json">
        {JSON.stringify(schemas)}
      </script>
    </Helmet>
  );
}

// Export configuration for use in other components
export { SITE_CONFIG };

export default SEO;